import { useEffect, useState, useMemo } from "react"
import { Flex, Dropdown, Button, Popover, Tooltip } from "antd"
import WorkflowHistory from "./WorkflowHistory"
import Workflow from "./Workflow"
import ChatRoom from "../chatRoom"
import ApprovalDetails from "./ApprovalDetails"
import { WechatOutlined, RetweetOutlined, DownOutlined, CloseOutlined, HistoryOutlined, TeamOutlined } from "@ant-design/icons"
import { useSelector } from "react-redux"
import { useTranslation } from "react-i18next"
import { resolveNodeTranslation } from "../../utils/workflowTranslations"
import { spaceColorBgMapping } from "../../utils/colorBgColorMap"
import { getShowApprovalTab } from "../../utils/requestHelper"

const RequestDetails = (props) => {

    const { spaceData = {}, workflowData, currentStatus, requestForm, workflowConditions, requestId, requestDetail, currentActiveForm, handleCycleChange, defaultRequestDetail, currentValues, notificationTemplates = [], activeForms = [], ...rest } = props
    const { userInfo, userGroups } = useSelector(state => state.user)
    const { spaceSpecificUserPickers = [] } = useSelector((state) => state.userRoleGroupState)
    const { t, i18n } = useTranslation()
    let showApprovalTab = getShowApprovalTab(requestDetail, userInfo, userGroups)
    const [activeTab, setActiveTab] = useState("workflow")
    const [showChatroom, setShowChatroom] = useState(false)
    const [showHistory, setShowHistory] = useState(false)
    const [showCollaborators, setShowCollaborators] = useState(false)
    const [currentCycle, setCurrentCycle] = useState(requestDetail?.currentCycle || "1")
    const [options, setOptions] = useState([])
    useEffect(() => {
        if (showApprovalTab && String(defaultRequestDetail.currentCycle) === String(requestDetail?.currentCycle)) {
            setActiveTab("approval")
        } else {
            setActiveTab("workflow")
        }
    }, [showApprovalTab, defaultRequestDetail.currentCycle, requestDetail?.currentCycle])

    const onChangeTab = (tab) => {
        setActiveTab(tab)
    }

    // Collaborators are added on a single step (ApprovalDetails > "Add Collaborator")
    // and then carried forward onto every following step. Group them under the
    // first step they appear on i.e. the step they were added in, in workflow order.
    const requestCollaborators = useMemo(() => {
        const steps = Array.isArray(requestDetail?.workflow) ? requestDetail.workflow : []
        const seenEmails = new Set()
        const groups = []
        steps.forEach(step => {
            const stepCollaborators = step?.collaborators?.user || []
            // The step label is stored canonically on the request; show it in the
            // viewer's language when the workflow node carries a translation.
            const workflowNode = workflowData?.node?.find(node => node?.data?.name?.toLowerCase() === String(step?.name || "").toLowerCase())
            const stepName = (workflowNode ? resolveNodeTranslation(workflowNode.data, i18n.language)?.name : "") || step?.name || ""
            const members = []
            stepCollaborators.forEach(collaborator => {
                const email = typeof collaborator === "string" ? collaborator : (collaborator?.email || collaborator?.username || "")
                if (!email || seenEmails.has(email)) return
                seenEmails.add(email)
                members.push({
                    email,
                    name: typeof collaborator === "string" ? "" : (collaborator?.username || collaborator?.name || "")
                })
            })
            if (members.length) groups.push({ stepName, members })
        })
        return groups
    }, [requestDetail?.workflow, workflowData?.node, i18n.language])

    const handleCycle = (index) => {
        setCurrentCycle(index)
        handleCycleChange(index)
        if (String(index) !== String(defaultRequestDetail.currentCycle)) {
            setActiveTab("workflow")
        }
    }

    useEffect(() => {
        let cycle = 1
        if (defaultRequestDetail.currentCycle) {
            cycle = Number(defaultRequestDetail.currentCycle)
            setCurrentCycle(defaultRequestDetail.currentCycle)
        }
        let cycleItems = []
        for (let index = 1; index <= cycle; index++) {
            cycleItems.push({
                label: <div onClick={() => handleCycle(index)}>{t('Cycle')} {index}</div>,
                key: index,
                value: index,
                onclick: (v) => {
                }
            })
        }
        setOptions(cycleItems)

    }, [defaultRequestDetail, userInfo])

    const closeChatroom = () => {
        handleShowChatroom(false)
    }

    const handleShowChatroom = (status) => {
        if (status) {
            setActiveTab("")
        } else {
            let currentStatus = showApprovalTab ? "approval" : "workflow"
            setActiveTab(currentStatus)
        }
        setShowHistory(false)
        setShowCollaborators(false)
        setShowChatroom(status)
    }

    const handleShowHistory = (status) => {
        if (status) {
            setActiveTab("")
        } else {
            let currentStatus = showApprovalTab ? "approval" : "workflow"
            setActiveTab(currentStatus)
        }
        setShowHistory(status)
        setShowChatroom(false)
        setShowCollaborators(false)
    }

    return (
        <Flex vertical style={{ paddingBottom: "1rem", height: '100%' }}>
            {
                !showChatroom &&
                <div>
                    <Flex align="center" justify={(showChatroom || showHistory) ? "flex-end" : "space-between"} style={{ marginBottom: "1.5rem" }}>
                        {
                            !showHistory &&
                            <Flex align="center" justify="space-between" className="request-details-tabs">
                                {
                                    showApprovalTab && String(defaultRequestDetail.currentCycle) === String(requestDetail?.currentCycle) &&
                                    <div className={`${activeTab === 'approval' ? 'selected-tab' : ''} request-tab`} onClick={() => onChangeTab("approval")}>{t('Approval Details')}</div>
                                }

                                <div className={`${activeTab === 'workflow' ? 'selected-tab' : ''} request-tab`} onClick={() => onChangeTab("workflow")}>{t('Workflow')}</div>
                                {/* <div className={`${activeTab === 'history' ? 'selected-tab' : ''} request-tab`} onClick={() => onChangeTab("history")}>History</div> */}
                            </Flex>
                        }
                        <Flex gap={12}>
                            <div className="request-cycle">
                                <Dropdown
                                    overlayStyle={{ textAlign: "end" }}
                                    menu={{
                                        items: options
                                    }}
                                    trigger={['click']}
                                >
                                    <Button style={{ width: "100%", borderRadius: 100, background: spaceData.spaceColor ? spaceColorBgMapping[spaceData.spaceColor] : "#FFF0F6", color: spaceData.spaceColor || "#EB2F96", gap: "5px", borderColor: "transparent", display: "flex", justifyContent: "space-between", alignItems: "center", padding: "8px 16px", fontSize: 14, fontWeight: 600 }}>
                                        <RetweetOutlined style={{ fontSize: 16, color: spaceData.spaceColor || "#EB2F96" }} />
                                        <span>{t('Cycle')}</span>
                                        <span>{currentCycle}</span>
                                        <DownOutlined style={{ fontSize: 14, color: spaceData.spaceColor || "#EB2F96" }} />

                                    </Button>
                                </Dropdown>

                            </div>
                            {
                                requestDetail.requestId &&
                                <>
                                    <Tooltip title={t('Activity Log')}>
                                        <div style={{ width: "2rem", height: "2rem", background: spaceData.spaceColor ? spaceColorBgMapping[spaceData.spaceColor] : "#FFF0F6", borderRadius: "1rem", display: "flex", justifyContent: "center", alignItems: "center", cursor: "pointer" }} onClick={() => handleShowHistory(!showHistory)}>
                                            {showHistory ? <CloseOutlined style={{ color: spaceData.spaceColor || "#EB2F96" }} />
                                                : <HistoryOutlined style={{ color: spaceData.spaceColor || "#EB2F96" }} />}
                                        </div>
                                    </Tooltip>
                                    {
                                        spaceData.enableChatroom ?
                                            <Tooltip title={t('Chatroom')}>
                                                <div style={{ width: "2rem", height: "2rem", background: spaceData.spaceColor ? spaceColorBgMapping[spaceData.spaceColor] : "#FFF0F6", borderRadius: "1rem", display: "flex", justifyContent: "center", alignItems: "center", cursor: "pointer" }} onClick={() => handleShowChatroom(!showChatroom)}>
                                                    {showChatroom ? <CloseOutlined style={{ color: spaceData.spaceColor || "#EB2F96" }} />
                                                        : <WechatOutlined style={{ color: spaceData.spaceColor || "#EB2F96" }} />}
                                                </div>
                                            </Tooltip>
                                        : null
                                    }
                                    <Popover
                                        open={showCollaborators}
                                        onOpenChange={setShowCollaborators}
                                        trigger="click"
                                        placement="bottomRight"
                                        rootClassName="collaborators-popover"
                                        title={t('Collaborators')}
                                        content={
                                            <div className="collaborators-content">
                                                {
                                                    requestCollaborators.length ?
                                                        requestCollaborators.map(group => (
                                                            <div key={group.stepName} className="collaborator-group">
                                                                <div className="collaborator-step-name">{group.stepName}</div>
                                                                {
                                                                    group.members.map(collaborator => (
                                                                        <div key={collaborator.email} className="collaborator-row">
                                                                            {collaborator.name ? `${collaborator.name} (${collaborator.email})` : collaborator.email}
                                                                        </div>
                                                                    ))
                                                                }
                                                            </div>
                                                        ))
                                                        : <span style={{ color: "#8C8C8C" }}>{t('No collaborators added to this request')}</span>
                                                }
                                            </div>
                                        }
                                    >
                                        <Tooltip title={t('Collaborators')}>
                                            <div style={{ width: "2rem", height: "2rem", background: spaceData.spaceColor ? spaceColorBgMapping[spaceData.spaceColor] : "#FFF0F6", borderRadius: "1rem", display: "flex", justifyContent: "center", alignItems: "center", cursor: "pointer" }}>
                                                <TeamOutlined style={{ color: spaceData.spaceColor || "#EB2F96" }} />
                                            </div>
                                        </Tooltip>
                                    </Popover>
                                </>
                            }
                        </Flex>

                    </Flex>
                    {
                        activeTab === "approval" ? (
                            <>
                                <ApprovalDetails requestForm={requestForm} currentStatus={currentStatus} workflowData={workflowData} requestId={requestId} requestDetail={requestDetail} defaultRequestDetail={defaultRequestDetail} conditions={props.conditions} workflowConditions={workflowConditions} currentActiveForm={currentActiveForm} spaceData={spaceData} currentValues={currentValues} definedWidgets={spaceSpecificUserPickers} notificationTemplates={notificationTemplates} {...rest} forms={activeForms} />
                            </>
                        ) : activeTab === 'workflow' ? <Workflow workflowData={workflowData} requestId={requestId} requestDetail={requestDetail} defaultRequestDetail={defaultRequestDetail} currentValues={currentValues} approvedStepLabel={rest.approvedStepLabel || ""} hideGooglePlacesResults={rest.hideGooglePlacesResults} getRequestDetail={rest.getRequestDetail} />
                            : null
                    }
                </div>
            }
            {
                requestId !== "new" && showChatroom && <ChatRoom showChatroom={showChatroom} closeChatroom={closeChatroom} spaceData={spaceData} requestDetail={defaultRequestDetail} notificationTemplates={notificationTemplates} {...rest} />
            }
            {
                showHistory ? <WorkflowHistory requestDetail={requestDetail} /> : null
            }
        </Flex>
    )
}

export default RequestDetails
